import { motion } from 'framer-motion';
import { ArrowRight, Award, Briefcase, Users, Code } from 'lucide-react';

const PROFILE_IMAGE = "https://customer-assets.emergentagent.com/job_66b41340-a1ce-4582-8b5b-792b2e2a5ffb/artifacts/ezhbt3ep__K2B0981.JPG";

const highlights = [
    { icon: Award, label: "Upwork", value: "Top Rated Plus" },
    { icon: Briefcase, label: "Experience", value: "10+ Years" },
    { icon: Users, label: "Clients", value: "400+" },
    { icon: Code, label: "Specialty", value: "Apps Script" },
];

export const Hero = () => {
    const scrollToContact = () => {
        const element = document.querySelector('#contact');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <section className="relative flex items-center pt-20 bg-white" data-testid="hero-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 w-full">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* Left Column - Text Content */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <a 
                            href="https://www.upwork.com/freelancers/spartha" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-[#F0FDF4] border border-[#DCFCE7] text-[#166534] px-4 py-1.5 rounded-full text-sm font-semibold mb-8 w-fit hover:bg-[#DCFCE7] transition-colors cursor-pointer"
                        >
                            <Award size={16} className="text-[#1E8E3E]" />
                            <span>Top Rated Plus on Upwork</span>
                            <span className="text-xs opacity-50 ml-1">↗</span>
                        </a>

                        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#2C2C2C] leading-tight mb-6">
                            The Google Sheets Expert {' '}
                            <span className="text-[#34A853]">Your Business Needs.</span>
                        </h1>

                        <p className="text-[#6B6B6B] leading-relaxed mb-8 max-w-lg">
                            Hi, I'm Parth. I turn your spreadsheets into automated business systems - from Google Sheets automation to custom dashboards and AI integrations.
                            Let's build something that saves you hours every week.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={scrollToContact}
                                className="group flex items-center justify-center gap-2 bg-[#34A853] hover:bg-[#2E9648] text-white px-8 py-4 rounded-full text-base font-medium shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
                                data-testid="hero-cta-primary"
                            >
                                Let's Chat (It's Free)
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
                            </button>
                        </div>
                    </motion.div>

                    {/* Right Column - Profile Image with Stats */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="relative flex justify-center lg:justify-end"
                    >
                        <div className="relative">
                            {/* Decorative border */}
                            <div className="absolute -top-4 -left-4 w-full h-full border-2 border-[#34A853]/20 rounded-2xl" />
                            
                            {/* Image container */}
                            <div className="relative w-72 h-80 md:w-80 md:h-96 rounded-2xl overflow-hidden shadow-card">
                                <img 
                                    src={PROFILE_IMAGE}
                                    alt="Parth - Digital Enabler"
                                    className="w-full h-full object-cover object-top"
                                    data-testid="hero-profile-image"
                                />
                                {/* Gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                            </div>

                            {/* Floating stats card */}
                            <div className="absolute -bottom-6 -right-6 md:-bottom-8 md:-right-8 bg-white rounded-2xl p-5 shadow-card border border-[#E5E5E5]">
                                <div className="grid grid-cols-2 gap-4">
                                    {highlights.map((item, index) => (
                                        <div key={index} className="text-center">
                                            <item.icon size={18} className="text-[#34A853] mx-auto mb-1" />
                                            <p className="text-[#2C2C2C] text-sm font-bold">{item.value}</p>
                                            <p className="text-[#6B6B6B] text-xs">{item.label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
